import { create } from 'zustand'

import type { TokenPair } from '../api/types'
import { purgeOfflineCaches } from '../pwa/cache'
import { useUiStore } from '../ui/ui-store'
import { refreshTokenStore } from './token-store'

export type SessionStatus = 'unknown' | 'guest' | 'authenticated'

export interface SessionUser {
  id: string
  email: string
  isAdmin?: boolean
  locale?: 'it' | 'en'
}

export interface Session {
  accessToken: string | null
  user: SessionUser | null
}

interface SessionState extends Session {
  status: SessionStatus
  setStatus: (status: SessionStatus) => void
  setSession: (session: Session) => void
  applyTokens: (tokens: TokenPair) => Promise<void>
  clearSession: () => void
}

/**
 * Client state only (D-042): session and identity, not server data.
 *
 * The access token lives in memory and is lost on reload; the HttpOnly
 * refresh cookie (web) or secure store (native) restores it. Logout purges
 * the offline caches and any native refresh token.
 */
export const useSessionStore = create<SessionState>()((set, get) => ({
  accessToken: null,
  user: null,
  // `unknown` until the startup orchestrator restores the cookie/token
  // session. The router guard shows the loader during this phase.
  status: 'unknown',

  setStatus: (status) => set({ status }),

  setSession: ({ accessToken, user }) =>
    set({ accessToken, user, status: 'authenticated' }),

  applyTokens: async (tokens) => {
    set((state) => ({
      accessToken: tokens.accessToken,
      status: 'authenticated',
      user: tokens.user
        ? {
            ...state.user,
            id: tokens.user.id,
            email: tokens.user.email,
          }
        : state.user,
    }))
    // Await the keystore write: a rotated refresh token that is not durable
    // makes the next cold start look like a logout.
    if (tokens.refreshToken) {
      await refreshTokenStore.set(tokens.refreshToken)
    }
  },

  clearSession: () => {
    const hadSession = Boolean(get().accessToken || get().user)
    set({ accessToken: null, user: null, status: 'guest' })
    useUiStore.getState().resetUiState()
    void refreshTokenStore.set(null)
    if (hadSession) void purgeOfflineCaches()
  },
}))

export const selectIsAuthenticated = (state: SessionState): boolean =>
  state.status === 'authenticated' && Boolean(state.accessToken)

export const selectIsAdmin = (state: SessionState): boolean =>
  state.status === 'authenticated' && state.user?.isAdmin === true
